import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosError, AxiosRequestConfig } from 'axios';

/**
 * RTK Query base query over the shared axios apiClient (ADR-004).
 *
 * Routing every RTK Query endpoint through apiClient preserves the hardened
 * auth path: cookie-only credentials, CSRF header injection/bootstrap, and the
 * 401 logout interceptor all live in `client.ts` and apply unchanged here.
 *
 * `client.ts` imports the Redux store (for its 401 interceptor), and the store
 * registers the RTK Query reducer — importing the client statically from here
 * would close a module cycle. The dynamic import defers resolution to first
 * use, after both modules are initialized.
 */
export interface RtkQueryArgs {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
}

export interface RtkQueryError {
  status?: number;
  message: string;
}

export const axiosBaseQuery =
  (): BaseQueryFn<RtkQueryArgs, unknown, RtkQueryError> =>
  async ({ url, method = 'get', body, params }) => {
    const { apiClient } = await import('./client');
    try {
      const res = await apiClient.request({ url, method, data: body, params });
      // Unwrap the standard ApiResponse envelope ({success, data}) the same
      // way the existing hand-rolled wrappers do.
      return { data: res.data?.data ?? res.data };
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: string; message?: string }>;
      return {
        error: {
          status: axiosError.response?.status,
          message:
            axiosError.response?.data?.error ??
            axiosError.response?.data?.message ??
            axiosError.message,
        },
      };
    }
  };
